import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { sendEmail, approvalEmailHtml, rejectionEmailHtml } from "@/lib/email";
import { reportBackgroundFailure } from "@/lib/errorReport";
import type { Profile, Student } from "@/types/database";

export interface PendingApplicant {
  // profile fields
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  status: Profile["status"];
  created_at: string;
  // student fields (joined; null if onboarding never reached the upsert)
  student: Pick<
    Student,
    | "id"
    | "student_number"
    | "institution"
    | "course"
    | "year_of_study"
    | "funding_type"
    | "nsfas_ref"
    | "nok_name"
    | "nok_relationship"
    | "nok_phone"
    | "nok_email"
    | "nok2_name"
    | "nok2_relationship"
    | "nok2_phone"
    | "nok2_email"
    | "id_document_url"
    | "proof_of_registration_url"
    | "nsfas_approval_url"
  > | null;
}

// =====================================================================
// Queue
// =====================================================================
export function usePendingApprovals() {
  const { propertyId } = useAuth();
  return useQuery({
    queryKey: ["approvals", "pending", propertyId],
    queryFn: async (): Promise<PendingApplicant[]> => {
      // Pending applicants may not have a property_id yet (onboarding can
      // happen before assignment). We scope by company via the joined
      // student row when present, but include unscoped pending rows too.
      const { data, error } = await supabase
        .from("profiles")
        .select(
          `id,full_name,email,phone,status,created_at,
           student:students!students_profile_id_fkey(
             id,student_number,institution,course,year_of_study,funding_type,
             nsfas_ref,nok_name,nok_relationship,nok_phone,nok_email,
             nok2_name,nok2_relationship,nok2_phone,nok2_email,
             id_document_url,proof_of_registration_url,nsfas_approval_url,
             property_id
           )`,
        )
        .eq("status", "pending_approval")
        .order("created_at", { ascending: true });
      if (error) throw error;

      const rows = (data ?? []) as Array<
        PendingApplicant & {
          student:
            | (PendingApplicant["student"] & { property_id: string | null })
            | (PendingApplicant["student"] & { property_id: string | null })[]
            | null;
        }
      >;

      return rows
        .map((r) => {
          // PostgREST returns 1-1 relations as a single object, but some
          // configs return an array — normalise.
          const stu = Array.isArray(r.student) ? r.student[0] : r.student;
          return { ...r, student: stu ?? null } as PendingApplicant & {
            student:
              | (PendingApplicant["student"] & { property_id: string | null })
              | null;
          };
        })
        .filter((r) => {
          // Show: rows with no student row yet (just submitted) OR rows
          // whose student is at this property OR whose property is unset.
          if (!r.student) return true;
          if (!r.student.property_id) return true;
          return r.student.property_id === propertyId;
        });
    },
  });
}

// Lightweight count hook for the sidebar badge — same query as above
// but only reads the count and is cheap to keep fresh.
export function usePendingApprovalsCount() {
  const { propertyId, role } = useAuth();
  return useQuery({
    queryKey: ["approvals", "count", propertyId],
    enabled: role === "admin" || role === "owner",
    refetchInterval: 60_000,
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from("profiles")
        .select("id", { count: "exact", head: true })
        .eq("status", "pending_approval");
      if (error) throw error;
      return count ?? 0;
    },
  });
}

// =====================================================================
// Mutations
// =====================================================================
export interface ApproveInput {
  applicantId: string;
  /** Property to assign the new resident to. Required — the student needs
   *  a property even if the room comes later. */
  propertyId: string;
  /** Company that owns the property (denormalised on students). */
  companyId: string;
  /** Optional: room within that property. Omit to approve without a room
   *  (admin will assign one later from the student profile page). */
  roomId?: string;
  /** Display name for the welcome notification. */
  propertyName?: string;
}

export function useApproveApplicant() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: ApproveInput) => {
      if (!user) throw new Error("Not signed in");
      const { applicantId, propertyId, roomId, companyId, propertyName } = input;
      const now = new Date().toISOString();

      // 1. Mark profile approved + assign property
      const { error: pErr } = await supabase
        .from("profiles")
        .update({
          status: "approved",
          property_id: propertyId,
          company_id: companyId,
          approved_by: user.id,
          approved_at: now,
          rejection_reason: null,
        })
        .eq("id", applicantId);
      if (pErr) throw new Error(`Profile update failed: ${pErr.message}`);

      // 2. Assign property (and optionally room) on the student row,
      //    mark active + onboarded
      const studentUpdate: Record<string, unknown> = {
        property_id: propertyId,
        company_id: companyId,
        onboarded_at: now,
        active: true,
      };
      if (roomId) studentUpdate.room_id = roomId;
      const { error: sErr } = await supabase
        .from("students")
        .update(studentUpdate)
        .eq("profile_id", applicantId);
      if (sErr) throw new Error(`Student update failed: ${sErr.message}`);

      // 3. If a room was assigned, mark it occupied
      if (roomId) {
        const { error: rErr } = await supabase
          .from("rooms")
          .update({ status: "occupied" })
          .eq("id", roomId);
        if (rErr) {
          // Non-blocking: the student has been approved & room assigned in the
          // students table; the rooms.status flag is a denormalised cache.
          // Surface to Sentry so we notice it drifting.
          reportBackgroundFailure("approvals.roomStatusUpdate", rErr, {
            roomId,
          });
        }
      }

      // 4. Notify the applicant
      const welcomeName = propertyName ?? "your new home";
      const body = roomId
        ? "Your application has been approved. You can now access the resident portal."
        : "Your application has been approved. Admin will assign your room shortly.";
      const { error: nErr } = await supabase.from("notifications").insert({
        user_id: applicantId,
        property_id: propertyId,
        type: "application_approved",
        title: `Welcome to ${welcomeName}!`,
        body,
        data: {
          approved_at: now,
          property_id: propertyId,
          ...(roomId ? { room_id: roomId } : {}),
        },
        read: false,
      });
      if (nErr) {
        reportBackgroundFailure("approvals.approve.notification", nErr, {
          applicantId,
        });
      }

      // 5. Send approval email
      if (input.applicantId) {
        const { data: prof } = await supabase
          .from("profiles")
          .select("full_name, email")
          .eq("id", applicantId)
          .maybeSingle();
        if (prof?.email) {
          await sendEmail({
            to: prof.email,
            subject: `Welcome to ${welcomeName}! Your application is approved`,
            html: approvalEmailHtml(prof.full_name ?? "Resident", welcomeName),
          });
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["approvals"] });
      qc.invalidateQueries({ queryKey: ["students"] });
      qc.invalidateQueries({ queryKey: ["rooms"] });
    },
  });
}

export function useRejectApplicant() {
  const qc = useQueryClient();
  const { user, propertyId } = useAuth();

  return useMutation({
    mutationFn: async ({
      applicantId,
      reason,
    }: {
      applicantId: string;
      reason: string;
    }) => {
      if (!user) throw new Error("Not signed in");
      if (!reason.trim()) throw new Error("Rejection reason is required");

      const { error: pErr } = await supabase
        .from("profiles")
        .update({
          status: "rejected",
          rejection_reason: reason.trim(),
          approved_by: user.id,
          approved_at: new Date().toISOString(),
        })
        .eq("id", applicantId);
      if (pErr) throw new Error(`Profile update failed: ${pErr.message}`);

      const { error: nErr } = await supabase.from("notifications").insert({
        user_id: applicantId,
        property_id: propertyId,
        type: "application_rejected",
        title: "Application not approved",
        body: reason.trim(),
        data: { rejected_at: new Date().toISOString() },
        read: false,
      });
      if (nErr) {
        reportBackgroundFailure("approvals.reject.notification", nErr, {
          applicantId,
        });
      }

      // Send rejection email
      const { data: prof } = await supabase
        .from("profiles")
        .select("full_name, email")
        .eq("id", applicantId)
        .maybeSingle();
      if (prof?.email) {
        await sendEmail({
          to: prof.email,
          subject: "Your M House application update",
          html: rejectionEmailHtml(prof.full_name ?? "Applicant", reason.trim()),
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["approvals"] });
    },
  });
}

// =====================================================================
// Helper — generate a short-lived signed URL to view a private doc
// =====================================================================
export async function viewStudentDoc(path: string): Promise<string | null> {
  if (!path) return null;
  const { data, error } = await supabase.storage
    .from("student-docs")
    .createSignedUrl(path, 300); // 5 min
  if (error) {
    reportBackgroundFailure("approvals.signedUrl", error, { path });
    return null;
  }
  return data?.signedUrl ?? null;
}
